import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import {
  getPlaybackExportStatus,
  requestPlaybackExport,
  type PlaybackExportFormat,
  type PlaybackExportStatus,
} from "@/api/fleet";
import { useToast } from "@/contexts/ToastContext";

interface Props {
  unitId: number;
  start: string;
  end: string;
  disabled?: boolean;
}

const FORMATS: PlaybackExportFormat[] = ["gpx", "kml", "csv", "pdf"];

export function ExportPlaybackButton({
  unitId,
  start,
  end,
  disabled,
}: Props) {
  const toast = useToast();
  const [pollId, setPollId] = useState<number | null>(null);
  const [format, setFormat] = useState<PlaybackExportFormat>("gpx");

  const requestM = useMutation({
    mutationFn: (formato: PlaybackExportFormat) =>
      requestPlaybackExport(unitId, { start, end, formato }),
    onSuccess: (res) => {
      setPollId(res.reportId);
      toast?.push("Export en cola. Descargando cuando esté listo…", "info");
    },
    onError: (err) =>
      toast?.push(`No se pudo encolar export: ${String(err)}`, "error"),
  });

  const statusQ = useQuery<PlaybackExportStatus>({
    queryKey: ["playback-export-status", pollId],
    enabled: pollId != null,
    queryFn: () => getPlaybackExportStatus(pollId as number),
    refetchInterval: (latest) => {
      const data = latest.state.data;
      if (data && (data.estado === "listo" || data.estado === "error")) {
        return false;
      }
      return 1500;
    },
  });

  const status = statusQ.data;

  // When listo with URL, auto-launch download.
  if (status?.estado === "listo" && status.downloadUrl) {
    const a = document.createElement("a");
    a.href = status.downloadUrl;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast?.push("Export listo y descargado.", "info");
    setPollId(null);
  } else if (status?.estado === "error") {
    toast?.push(
      `Export falló: ${status.errorMensaje ?? "error desconocido"}`,
      "error"
    );
    setPollId(null);
  }

  return (
    <div className="inline-flex items-center gap-2">
      <select
        className="select text-xs"
        value={format}
        onChange={(e) => setFormat(e.target.value as PlaybackExportFormat)}
        disabled={disabled || requestM.isPending || pollId != null}
      >
        {FORMATS.map((f) => (
          <option key={f} value={f}>
            {f.toUpperCase()}
          </option>
        ))}
      </select>
      <button
        className="btn-primary text-xs"
        disabled={disabled || requestM.isPending || pollId != null}
        onClick={() => requestM.mutate(format)}
        data-testid={`playback-export-${format}`}
      >
        {pollId != null
          ? "Generando…"
          : requestM.isPending
            ? "Encolando…"
            : `Exportar ${format.toUpperCase()}`}
      </button>
    </div>
  );
}
